import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Avatar5 from '../../../assets/img/avatar_5.svg';
import ComponentCircleLoader from '../../../components/Loaders/ComponentCircleLoader';
import NoDataComponent from '../../../components/NoDataComponent/NoDataComponent';
import editIcon from '../../../assets/img/icons/edit-pen.svg';
import {
  Container,
  CommonText,
  CommonFlex,
  ColoredText,
  CommonImage,
  RoundLetterSection
} from '../../../styledComponents/common';
// import {
//   MembersTableHeadContainer,
//   MembersTableContainer,
//   TableItem,
//   TableText,
// }
import { TableItem } from 'styledComponents/teams';
import { TableText } from 'styledComponents/common';
import {
  PendingRequestsTableHeadContainer,
  PendingRequestsTableContainer,
  UserSettings,
  RadioButton
} from 'styledComponents/members';
import { CommImageNameComp } from '../CommonComponents';
import ToggleButtonComponent from 'components/ToggleButton/ToggleButton';
import { RoundIcon } from 'custom_modules/Tasks/tasksStyles';
import TaskActionTooltip from 'components/Tooltip/TaskActionTooltip';

// Add this style for the type columns that contain radio buttons
const typeColumnStyle = {
  minWidth: '280px',  // Increased width for type columns
  width: '280px'
};

// Updated dummy data without user_code
const dummyData = [
  {
    id: 1,
    first_name: 'John',
    last_name: 'Doe',
    avatar: null,
    mac_address: 'AA:BB:CC:DD:EE:FF',
    email: 'john.doe@example.com',
    role: 'Manager',
    office: { id: 1, name: 'Headquarters' },
    current_employee_type: 'Remote User',
    new_employee_type: 'Office User',
    updated_date: '2023-10-15',
    updated_type: 'office',
    requester: {
      first_name: 'Admin',
      last_name: 'User',
      avatar: null
    }
  },
  {
    id: 2,
    first_name: 'Jane',
    last_name: 'Smith',
    avatar: null,
    mac_address: 'FF:EE:DD:CC:BB:AA',
    email: 'jane.smith@example.com',
    role: 'Developer',
    office: { id: 2, name: 'Branch Office' },
    current_employee_type: 'Office User',
    new_employee_type: 'Remote User',
    updated_date: '2023-10-16',
    updated_type: 'remote',
    requester: {
      first_name: 'Team',
      last_name: 'Lead',
      avatar: null
    }
  },
  {
    id: 3,
    first_name: 'Robert',
    last_name: 'Johnson',
    avatar: null,
    mac_address: '11:22:33:44:55:66',
    email: 'robert.johnson@example.com',
    role: 'Designer',
    office: { id: 3, name: 'Remote' },
    current_employee_type: 'Mashaer User',
    new_employee_type: 'Office User',
    updated_date: '2023-10-17',
    updated_type: 'office',
    requester: {
      first_name: 'HR',
      last_name: 'Manager',
      avatar: null
    }
  },
  {
    id: 4,
    first_name: 'Emily',
    last_name: 'Williams',
    avatar: null,
    mac_address: '66:55:44:33:22:11',
    email: 'emily.williams@example.com',
    role: 'Analyst',
    office: { id: 1, name: 'Headquarters' },
    current_employee_type: 'Office User',
    new_employee_type: 'Mashaer User',
    updated_date: '2023-10-18',
    updated_type: 'mashaer',
    requester: {
      first_name: 'Department',
      last_name: 'Head',
      avatar: null
    }
  },
  {
    id: 5,
    first_name: 'Michael',
    last_name: 'Brown',
    avatar: null,
    mac_address: 'AB:CD:EF:12:34:56',
    email: 'michael.brown@example.com',
    role: 'Supervisor',
    office: { id: 2, name: 'Branch Office' },
    current_employee_type: 'Remote User',
    new_employee_type: 'Mashaer User',
    updated_date: '2023-10-19',
    updated_type: 'mashaer',
    requester: {
      first_name: 'Operations',
      last_name: 'Director',
      avatar: null
    }
  }
];

const PendingRequestMemberTable = props => {
  const { t } = useTranslation();
  const [isUserTypeChangeModalOpen, setIsUserTypeChangeModalOpen] =
    useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const [role, setRole] = useState('');

  // Use the pendingRequests data from props
  const membersList = props.membersList || [];

  const handleSelectMember = (member) => {
    setSelectedMembers(prev => {
      // Check if this member is already selected (by ID)
      const isSelected = prev.some(item => item.id === member.id);

      const newSelection = isSelected
        ? prev.filter(item => item.id !== member.id) // Remove member if already selected
        : [...prev, member]; // Add the entire member object

      // Pass selected members to parent component
      if (props.onSelectionChange) {
        props.onSelectionChange(newSelection);
      }

      return newSelection;
    });
  };

  const handleSelectAll = () => {
    let newSelection = [];

    if (!selectAll) {
      // Add all member objects to selection
      newSelection = [...membersList];
    }

    // Pass selected members to parent component
    if (props.onSelectionChange) {
      props.onSelectionChange(newSelection);
    }

    setSelectedMembers(newSelection);
    setSelectAll(!selectAll);
  };

  useEffect(() => {
    setRole(localStorage.getItem('user_role'));
  }, []);

  useEffect(() => {
    if (membersList && membersList.length > 0) {
      // Check if all members are selected by comparing lengths and IDs
      const allSelected =
        selectedMembers.length === membersList.length &&
        membersList.every(member =>
          selectedMembers.some(selected => selected.id === member.id)
        );

      setSelectAll(allSelected);
    }
  }, [selectedMembers, membersList]);

  // Helper function to get user type display text
  const getUserTypeDisplay = (type) => {
    switch(type) {
      case 'office':
        return t('office_user');
      case 'remote':
        return t('remote_user');
      case 'mashaer':
        return t('mashaer_user');
      default:
        return type || '-';
    }
  };

  const getStatusDisplay = (is_active, is_approved) => {
    if (is_active && is_approved) {
      return <TableText>{t('approved')}</TableText>
    }
    if (!is_active && !is_approved) {
      return <TableText>{t('declined')}</TableText>
    }
    if (is_active && !is_approved) {
      return <TableText>{t('pending')}</TableText>
    }
  };

  return (
    <Container style={{ paddingBottom: `20px` }}>
      {props.isLoading ? (
        <ComponentCircleLoader />
      ) : (
        <>
          <PendingRequestsTableHeadContainer
            style={{ borderBottom: `1px solid #C2CCE1` }}
          >
            <TableItem>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  style={{
                    cursor: 'pointer',
                    width: '22px',
                    height: '22px',
                    marginRight: '10px',
                    accentColor: '#313131',
                    borderRadius: '7px',
                    border: '1.5px solid #C2CCE1'
                  }}
                />
              </div>
            </TableItem>
            <TableItem>
              <TableText>{t('member_name')}</TableText>
            </TableItem>
            <TableItem>
              <TableText>{t('email')}</TableText>
            </TableItem>
            <TableItem>
              <TableText>{t('role')}</TableText>
            </TableItem>
            <TableItem>
              <TableText>{t('office')}</TableText>
            </TableItem>
            <TableItem>
              <TableText>{t('user_type')}</TableText>
            </TableItem>
            <TableItem>
              <TableText>{t('action_status')}</TableText>
            </TableItem>
            <TableItem>
              <TableText>{t('date')}</TableText>
            </TableItem>
            <TableItem>
              <TableText>{t('requested_by')}</TableText>
            </TableItem>
          </PendingRequestsTableHeadContainer>
          {membersList && membersList.length > 0 ? (
            membersList.map((member, index) => (
              member.user_id !== null && (
              <PendingRequestsTableContainer key={index}>
                <TableItem>
                  <input
                    type="checkbox"
                    checked={selectedMembers.some(item => item.id === member.id)}
                    onChange={() => handleSelectMember(member)}
                    style={{
                      cursor: 'pointer',
                      width: '22px',
                      height: '22px',
                      accentColor: '#313131',
                      borderRadius: '7px',
                      border: '1.5px solid #C2CCE1'
                    }}
                  />
                </TableItem>
                <TableItem>
                  <div>
                    <CommImageNameComp
                      imgSource={member?.avatar ? member.avatar : Avatar5}
                      name={`${member.first_name} ${member.last_name}`}
                      size="36px"
                    />
                  </div>
                </TableItem>
                <TableItem>
                  <TableText>{member.email || `-`}</TableText>
                </TableItem>
                <TableItem>
                  <TableText>{member.role || `-`}</TableText>
                </TableItem>
                <TableItem>
                  <TableText>
                    {member.office && member.office.name
                      ? member.office.name
                      : `-`}
                  </TableText>
                </TableItem>
                <TableItem>
                  <TableText>{getUserTypeDisplay(member.user_type)}</TableText>
                </TableItem>
                <TableItem>
                  {
                    getStatusDisplay(member.is_active, member.is_approved)
                  }
                </TableItem>
                <TableItem>
                  <TableText>
                    {member.created_at ? new Date(member.created_at).toLocaleDateString() : `-`}
                  </TableText>
                </TableItem>
                <TableItem>
                    <RoundIcon
                      type="edit"
                    >
                      <CommonImage src={member.created_by?.avatar ? member.created_by.avatar : Avatar5} alt="" size="22px" />
                      <TaskActionTooltip toolTipText={`${member.created_by?.first_name} ${member.created_by?.last_name}`} />
                    </RoundIcon>
                  </TableItem>
                </PendingRequestsTableContainer>
              )
            ))
          ) : (
            <NoDataComponent
              title={t('no_pending_requests_found')}
              padding="5vh 0"
              imageHeight="140px"
            />
          )}
        </>
      )}
    </Container>
  );
};

export default PendingRequestMemberTable;
